import { createContext, ReactNode, useCallback, useContext, useEffect, useState } from 'react';
import translations from './translations.json';

export const languages = [
  { code: 'en', name: 'English' }, { code: 'de', name: 'Deutsch' },
  { code: 'fr', name: 'Français' }, { code: 'nl', name: 'Nederlands' },
  { code: 'es', name: 'Español' }, { code: 'it', name: 'Italiano' },
  { code: 'pl', name: 'Polski' },
] as const;
export type Language = typeof languages[number]['code'];
export const languageStorageKey = 'tronicsbase.language';
export const isLanguage = (value: unknown): value is Language => languages.some(language => language.code === value);
const catalog: Record<string, Record<string, string>> = translations;
type Translate = (key: string, values?: Record<string, string | number>) => string;
const LanguageContext = createContext<{ language: Language; setLanguage: (language: Language) => void; t: Translate } | null>(null);

function initialLanguage(): Language {
  try {
    const saved = localStorage.getItem(languageStorageKey);
    if (isLanguage(saved)) return saved;
  } catch { /* Storage may be unavailable in private browsing. */ }
  for (const preference of navigator.languages?.length ? navigator.languages : [navigator.language]) {
    const code = preference.toLowerCase().split('-')[0];
    if (isLanguage(code)) return code;
  }
  return 'en';
}

export function LanguageProvider({ children }: { children: ReactNode }) {
  const [language, updateLanguage] = useState<Language>(initialLanguage);
  const setLanguage = useCallback((next: Language) => {
    if (!isLanguage(next)) return;
    updateLanguage(next);
    try { localStorage.setItem(languageStorageKey, next); } catch { /* Selection still works without storage. */ }
  }, []);
  const t = useCallback<Translate>((key, values) => {
    const translated = language === 'en' ? key : catalog[language]?.[key] ?? key;
    return translated.replace(/\{(\w+)\}/g, (match, name) => String(values?.[name] ?? match));
  }, [language]);
  useEffect(() => {
    document.documentElement.lang = language;
    document.title = t('TronicsBase — Your next idea, in your pocket');
    document.querySelector('meta[name="description"]')?.setAttribute('content', t('Meet TronicsBase: the app for electronics projects, components, community, and TBC rewards.'));
  }, [language, t]);
  useEffect(() => {
    const sync = (event: StorageEvent) => {
      if (event.key === languageStorageKey && isLanguage(event.newValue)) updateLanguage(event.newValue);
    };
    window.addEventListener('storage', sync);
    return () => window.removeEventListener('storage', sync);
  }, []);
  return <LanguageContext.Provider value={{ language, setLanguage, t }}>{children}</LanguageContext.Provider>;
}

export function useI18n() {
  const context = useContext(LanguageContext);
  if (!context) throw new Error('useI18n must be used inside LanguageProvider');
  return context;
}
